<script>
import { mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { formatSlug } from '@/support/utils/stringHelper'
import { cnpjValidator, sanitizeCnpj } from '@/support/customValidators/cnpjValidator'
import wizardForm from '@/mixins/wizardForm'

export default {
  name: 'ManagementPartnerEdit',
  components: {
    Action: () => import('@/components/general/Action'),
    ActionBar: () => import('@/components/general/ActionBar'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FormSection: () => import('@/components/general/FormSection'),
    InputField: () => import('@/components/general/InputField'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    Upload: () => import('@/components/general/Upload')
  },

  mixins: [ wizardForm ],

  data () {
    return {
      backUrl: {
        name: 'management.partners'
      },

      isAliasDuplicate: false,

      formData: {
        id: null,
        name: null,
        cnpj: null,
        alias: null,
        description: null,
        imageLogo: null,
        commissionRate: null,
        active: 0
      }
    }
  },

  validations: {
    formData: {
      name: { required },
      alias: {
        required,
        aliasDuplicate: function () {
          return !this.isAliasDuplicate
        }
      },

      cnpj: {
        required,
        cnpjValidation: function (value) {
          return cnpjValidator(value)
        }
      },

      description: { required },
      imageLogo: { required },
      commissionRate: { required },
      active: { required }
    }
  },

  computed: {
    isEditing () {
      return this.$route.params.id
    }
  },

  watch: {
    'formData.name' () {
      if (!this.isEditing) {
        this.formData.alias = formatSlug(this.formData.name)
      }
    },

    'formData.alias' (event) {
      this.formData.alias = formatSlug(event)

      this.debounceEvent(async () => {
        this.setFetching(true)
        const search = { name: this.formData.name }

        const partners = await this.attemptListPartners({ query: search })
          .then(pagination => { return pagination.data.data })

        this.isAliasDuplicate = this.isEditing ? partners.length && partners.some(partner => partner.id !== this.formData.id) : partners.length > 0

        this.$v.formData.alias.$touch()

        this.setFetching(false)
      }, 1000)
    }
  },

  created () {
    if (this.isEditing) {
      this.setFetching(true)

      this.attemptGetManagementPartner(this.$route.params.id)
        .then(({ data }) => {
          this.formData = {
            ...this.formData,
            ...data
          }

          this.formData.active = this.formData.active ? 1 : 0

          this.$nextTick(() => {
            this.snapshotWizardState()
          })
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('management.partner:load:fetch.error') })
          this.$router.push(this.backUrl)
        })
        .finally(() => {
          this.setFetching(false)
        })
    } else {
      this.snapshotWizardState()
    }
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetManagementPartner',
      'attemptListPartners',
      'attemptCreateManagementPartner',
      'attemptUpdateManagementPartner'
    ]),

    leave () {
      this.$router.push(this.backUrl)
    },

    getErrorMessage (response) {
      return response && response.data && response.data.error
        ? response.data.error.message
        : null
    },

    submitCreation () {
      this.setFetching(true)
      const formData = this.formatFormData()

      this.attemptCreateManagementPartner(formData)
        .then(() => {
          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('management.partner:feedback.created.success') })
        })
        .catch((response) => {
          if (this.getErrorMessage(response) === 'partner__alias_already_exists') {
            this.setFeedback({ message: this.$t('management.partner:feedback.alias.duplicate.error') })

            return
          }

          this.setFeedback({ message: this.$t('management.partner:feedback.save.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    submitUpdate () {
      this.setFetching(true)
      const formData = this.formatFormData()

      this.attemptUpdateManagementPartner(formData)
        .then(() => {
          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('management.partner:feedback.updated.success') })
        })
        .catch((response) => {
          if (this.getErrorMessage(response) === 'partner__alias_already_exists') {
            this.setFeedback({ message: this.$t('management.partner:feedback.alias.duplicate.error') })

            return
          }

          this.setFeedback({ message: this.$t('management.partner:feedback.save.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    submit () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.formData.id ? this.submitUpdate() : this.submitCreation()
      } else {
        this.setFeedback({ message: this.$t('global:feedback.validation.error') })
      }
    },

    formatFormData () {
      const data = {}

      data.id = this.formData.id || null
      data.name = this.formData.name
      data.alias = this.formData.alias
      data.cnpj = sanitizeCnpj(this.formData.cnpj)
      data.description = this.formData.description
      data.image_logo = this.formData.imageLogo
      data.commission_rate = this.formData.commissionRate
      data.active = this.formData.active ? 1 : 0

      return data
    }
  }
}
</script>

<template>
  <div class="main-content management-banner-create">
    <ContentHeader
      :title="isEditing ? formData.name : $t('management.partner.edit:header.title')"
      dark-theme
      sticky
      fixed
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.management')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave()"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          :text="$t('global:form.save')"
          type="button"
          dark
          flat
          @click="submit()"
        />
      </template>
    </ContentHeader>

    <div class="main-content-inner">
      <div class="center">
        <div class="management-banner-create-header">
          <h2 class="management-banner-create-title">
            {{ $t('management.partner.edit:main.title') }}
          </h2>
        </div>
        <form
          class="form"
          @submit.prevent="submit()"
        >
          <InputField
            v-model="formData.name"
            :label="$t('global:form.management.partner.edit:title')"
            :validation="$v.formData.name"
            :counter="50"
          />

          <InputField
            v-model="formData.alias"
            :label="$t('global:form.management.partner.edit:alias')"
            :validation="$v.formData.alias"
            :counter="100"
          />

          <InputField
            v-model="formData.cnpj"
            :label="$t('global:form.cnpj')"
            mask="##.###.###/####-##"
            alphanumeric
            :validation="$v.formData.cnpj"
          />

          <InputField
            v-model="formData.description"
            :label="$t('global:form.management.partner.edit:description')"
            :validation="$v.formData.description"
            :counter="100"
          />
          <InputField
            v-model="formData.commissionRate"
            type="number"
            :min="0"
            :max="100"
            :label="$t('global:form.management.partner.edit:commissionRate')"
            :under-description="$t('global:form.management.partner.edit:commissionRate.under-description')"
            :is-round-value-number="false"
            :validation="$v.formData.commissionRate"
          />
          <FormSection :title="$t('global:form.management.partner.edit:partner.imageLogo')">
            <Upload
              v-model="formData.imageLogo"
              icon="image-multiple"
              :label="$t('global:form.management.partner.edit:partner.imageLogo.add')"
              :description="$t('global:form.management.partner.edit:background.size')"
              cropper
              :width="44"
              :height="44"
              :preview="4"
              :validation="$v.formData.imageLogo"
            />
          </FormSection>
        </form>
      </div>
    </div>
    <ModalConfirm
      :active="modalLeaveOpen"
      :title="$t('global:form.leave.title')"
      :description="$t('global:form.leave.description')"
      @confirmAction="confirmWizardLeave"
      @cancelAction="cancelWizardLeave"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/banners.css"></style>
